import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import SvgCanvas from '@svgedit/svgcanvas';
import StyleProvider from './StyleProvider';
import DielineToSvgConverter from './DielineToSvgConverter';
import { StandupPouchModel } from './StandupPouchModel';
import PdfUploader from './PdfUploader';
import { ApiDataAccess } from '../infrastructure/ApiDataAccess';

import './Render.css';

const VectorStudioFinal = ({ width: propWidth, height: propHeight, layers }) => {
    const width = useMemo(() => parseInt(propWidth, 10) || 800, [propWidth]);
    const height = useMemo(() => parseInt(propHeight, 10) || 600, [propHeight]);

    const isUpdatingPropsRef = useRef(false);
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const rendererRef = useRef(null);
    const viewportMainRef = useRef(null); // Reference for handling right-click dragging viewport movement

    // Ref to hold a list of copied element deep-clones
    const copiedElementsRef = useRef([]);

    const [canvasLayers, setCanvasLayers] = useState([]);
    const [activeLayer, setActiveLayer] = useState('');
    const [zoom, setZoom] = useState(1);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [svgString, setSvgString] = useState('');

    // Controls the visibility of the basic shapes flyout menu
    const [isShapeMenuOpen, setIsShapeMenuOpen] = useState(false);
    // Control state for the Sidebar Window Popup Modal
    const [isCustomPopupOpen, setIsCustomPopupOpen] = useState(false);
    // Control state for the new text input inside the popup modal
    const [popupInputValue, setPopupInputValue] = useState('');

    // Anchor reference to position the fixed popup dynamically next to the button
    const shapeButtonRef = useRef(null);

    const fillInputRef = useRef(null);
    const strokeInputRef = useRef(null);
    const thicknessInputRef = useRef(null);
    const textInputRef = useRef(null);
    const fontFamilyInputRef = useRef(null);

    const styles = StyleProvider.GetStyles(width, height);
    const dielineToSvgConverter = useMemo(() => new DielineToSvgConverter(layers), [layers]);

    const syncLayersFromCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const drawing = canvas.getCurrentDrawing();
        if (!drawing) return;

        const layerNames = [];
        const numLayers = drawing.getNumLayers();

        for (let i = 0; i < numLayers; i++) {
            layerNames.push(drawing.getLayerName(i));
        }

        setCanvasLayers(layerNames);
        setActiveLayer(drawing.getCurrentLayerName());
    }, []);

    const updateProperties = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || isUpdatingPropsRef.current) return;

        // Set the lock
        isUpdatingPropsRef.current = true;

        const selectedElems = canvas.getSelectedElements();
        if (selectedElems && selectedElems[0]) {
            const activeElem = selectedElems[0];
            const fill = activeElem.getAttribute('fill');
            const stroke = activeElem.getAttribute('stroke');
            const strokeWidth = activeElem.getAttribute('stroke-width');
            const isText = activeElem.tagName === 'text';
            const fontFamily = activeElem.getAttribute('font-family');

            // Prevent updating the DOM input if the value matches to avoid cycle triggers
            if (fill && fillInputRef.current && !fill.includes('url(') && fillInputRef.current.value !== fill) {
                fillInputRef.current.value = fill;
            }
            if (stroke && strokeInputRef.current && !stroke.includes('url(') && strokeInputRef.current.value !== stroke) {
                strokeInputRef.current.value = stroke;
            }
            if (strokeWidth && thicknessInputRef.current && thicknessInputRef.current.value !== strokeWidth) {
                thicknessInputRef.current.value = strokeWidth;
            }

            if (textInputRef.current) {
                textInputRef.current.disabled = !isText;
                textInputRef.current.value = isText ? activeElem.textContent : '';
            }

            if (fontFamilyInputRef.current) {
                fontFamilyInputRef.current.disabled = !isText;
                if (isText && fontFamily) {
                    fontFamilyInputRef.current.value = fontFamily.replace(/['"]/g, '');
                } else if (isText) {
                    fontFamilyInputRef.current.value = 'sans-serif';
                }
            }
        } else {
            if (textInputRef.current) {
                textInputRef.current.value = '';
                textInputRef.current.disabled = true;
            }
            if (fontFamilyInputRef.current) {
                fontFamilyInputRef.current.disabled = true;
            }
        }

        // Release the lock safely in the next macro-task to clear event queues
        setTimeout(() => {
            isUpdatingPropsRef.current = false;
        }, 0);
    }, []);

    const handlerRefs = useRef({ updateProperties, syncLayersFromCanvas });
    useEffect(() => {
        handlerRefs.current = { updateProperties, syncLayersFromCanvas };
    }, [updateProperties, syncLayersFromCanvas]);

    const canvasChangeProxy = useCallback(() => {
        handlerRefs.current.updateProperties();
        update3DModel();
    }, []);


    const injectDefinitionsIntoSvgString = (rawSvgString) => {
        try {
            // Find the root SVG's <defs> element in the live DOM
            const svgRoot = containerRef.current?.querySelector('svg');
            const globalDefs = svgRoot?.querySelector('defs');

            if (!globalDefs || !globalDefs.innerHTML.trim()) {
                return rawSvgString; // No gradients/defs to inject
            }

            // Parse the raw exported SVG string into a real DOM structure
            const parser = new DOMParser();
            const doc = parser.parseFromString(rawSvgString, 'image/svg+xml');

            // Check for XML parsing errors
            const parserError = doc.querySelector('parsererror');
            if (parserError) {
                console.error("SVG contains string syntax errors before injection:", parserError.textContent);
                return rawSvgString;
            }

            // Locate or create a <defs> tag inside our newly parsed document object
            let docDefs = doc.querySelector('defs');
            if (!docDefs) {
                docDefs = doc.createElementNS("http://www.w3.org/2000/svg", "defs");
                // Always insert <defs> as the very first child of the root <svg> element
                doc.documentElement.insertBefore(docDefs, doc.documentElement.firstChild);
            }

            // Safely port over all hidden layout definitions (Gradients, ClipPaths, Patterns)
            docDefs.innerHTML = globalDefs.innerHTML;

            // Convert the clean DOM object back into a perfect XML string sequence
            return new XMLSerializer().serializeToString(doc);
        } catch (err) {
            console.error("Failed to parse and patch gradient configurations onto SVG data:", err);
            return rawSvgString;
        }
    };

    const update3DModel = async () => {
        var rawSvgString = await canvasRef.current.getSvgString();

        // Fix potential native NaN dimension outputs from canvas initialization
        rawSvgString = rawSvgString.replace(/width="NaN"/g, `width="${width}"`);
        rawSvgString = rawSvgString.replace(/height="NaN"/g, `height="${height}"`);

        // Force ensure a strict viewBox attribute is present in the outgoing string
        if (!rawSvgString.includes('viewBox')) {
            rawSvgString = rawSvgString.replace('<svg', `<svg viewBox="0 0 ${width} ${height}"`);
        }

        // Port definitions safely using DOM parser pipeline
        const svgString = injectDefinitionsIntoSvgString(rawSvgString);

        const pngDataUrl = await convertSvgToImage(svgString, {
            width: width,
            height: 420,
            rotation: 0,
            backgroundColor: '#e0e0e0'
        });

        rendererRef.current.refreshDesign(pngDataUrl);
    };

    const exportSVG = async () => {
        if (!canvasRef.current) return;
        try {
            let rawSvgString = canvasRef.current.getSvgString();
            if (!rawSvgString) throw new Error("Export returned empty payload.");

            // Fix potential native NaN dimension outputs from canvas initialization
            rawSvgString = rawSvgString.replace(/width="NaN"/g, `width="${width}"`);
            rawSvgString = rawSvgString.replace(/height="NaN"/g, `height="${height}"`);

            // Force ensure a strict viewBox attribute is present in the outgoing string
            if (!rawSvgString.includes('viewBox')) {
                rawSvgString = rawSvgString.replace('<svg', `<svg viewBox="0 0 ${width} ${height}"`);
            }

            // Port definitions safely using DOM parser pipeline
            const svgString = injectDefinitionsIntoSvgString(rawSvgString);

            // Post the payload directly to your C# controller endpoint
            const response = await fetch('api/dieline/artwork', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ svg: svgString })
            });

            if (!response.ok) {
                throw new Error(`Server returned status ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'artwork_cmyk_production.pdf';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

        } catch (error) {
            console.error("Failed to export SVG and download PDF:", error);
            alert("Failed to download production PDF. Check console logs for error context.");
        }
    };


    const convertSvgToImage = (svgString, { format = 'image/png', width, height, rotation = 0, backgroundColor } = {}) => {
        return new Promise((resolve, reject) => {
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const URL = window.URL || window.webkitURL || window;
            const blobUrl = URL.createObjectURL(svgBlob);

            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                const targetWidth = width || img.naturalWidth || 800;
                const targetHeight = height || img.naturalHeight || 600;

                canvas.width = targetWidth;
                canvas.height = targetHeight;

                if (backgroundColor) {
                    ctx.fillStyle = backgroundColor;
                    ctx.fillRect(0, 0, targetWidth, targetHeight);
                }

                const degrees = ((rotation % 360) + 360) % 360;

                if (degrees === 90) {
                    ctx.translate(targetWidth, 0);
                    ctx.rotate((90 * Math.PI) / 180);
                    ctx.drawImage(img, 0, 0, targetHeight, targetWidth);
                } else if (degrees === 180) {
                    ctx.translate(targetWidth, targetHeight);
                    ctx.rotate((180 * Math.PI) / 180);
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                } else if (degrees === 270) {
                    ctx.translate(0, targetHeight);
                    ctx.rotate((270 * Math.PI) / 180);
                    ctx.drawImage(img, 0, 0, targetHeight, targetWidth);
                } else {
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                }

                const imageResult = canvas.toDataURL(format);
                URL.revokeObjectURL(blobUrl);
                resolve(imageResult);
            };

            img.onerror = (err) => {
                URL.revokeObjectURL(blobUrl);
                reject(new Error('Failed to convert SVG to image: ' + err.message));
            };

            img.src = blobUrl;
        });
    };

    const addDielineLayer = useCallback((canvasInstance) => {
        const drawing = canvasInstance?.getCurrentDrawing();
        if (!canvasInstance || !drawing) return;

        const backgroundLayerName = drawing.getLayerName(0);
        if (!backgroundLayerName) return;

        const backgroundLayerGroup = drawing.getLayerByName(backgroundLayerName);
        if (!backgroundLayerGroup) return;

        const customSvgString = dielineToSvgConverter.SVG;
        const svgNS = "http://www.w3.org/2000/svg";
        const tempGroup = document.createElementNS(svgNS, "g");

        tempGroup.innerHTML = customSvgString;
        const importedNode = tempGroup.firstElementChild;

        if (!importedNode) {
            console.error("Failed to parse the SVG string.");
            return;
        }

        if (!importedNode.hasAttribute('x')) importedNode.setAttribute('x', '0');
        if (!importedNode.hasAttribute('y')) importedNode.setAttribute('y', '0');

        importedNode.setAttribute("id", canvasInstance.getNextId());
        importedNode.style.pointerEvents = "all";
        importedNode.setAttribute("transform", "translate(-70, -80)");

        backgroundLayerGroup.appendChild(importedNode);
        canvasInstance.updateCanvas();

        update3DModel();
    }, [dielineToSvgConverter]);

    const zooming = useCallback((parsedZoom) => {
        if (canvasRef.current) {
            canvasRef.current.setZoom(parsedZoom);
            canvasRef.current.updateCanvas();

            const rootSvg = containerRef.current?.querySelector('svg');
            if (rootSvg) {
                rootSvg.setAttribute('width', width * parsedZoom);
                rootSvg.setAttribute('height', height * parsedZoom);
            }
        }
        update3DModel();
    }, [width, height]);

    const handleZoomChange = useCallback((newZoom) => {
        const parsedZoom = Math.max(1.0, Math.min(5, Number(newZoom)));
        setZoom(parsedZoom);
        zooming(parsedZoom);
    }, [zooming]);

    useEffect(() => {
        if (!containerRef.current) return;

        const canvas = new SvgCanvas(containerRef.current, {
            canvas_width: width,
            canvas_height: height,
            initFill: { color: 'FF0000', opacity: 1 },
            initStroke: { color: '000000', opacity: 1, width: 3 },
        });
        canvasRef.current = canvas;

        if (canvas.textActions && textInputRef.current) {
            canvas.textActions.setInputElem(textInputRef.current);
        }

        canvas.setResolution(width, height);
        canvas.setMode('select');

        canvas.renameCurrentLayer("Dieline");
        addDielineLayer(canvas);
        canvas.createLayer("Artwork");
        canvas.identifyLayers();
        canvas.setCurrentLayer("Artwork");
        canvas.updateCanvas();

        handlerRefs.current.syncLayersFromCanvas();

        // 1. KEYBOARD NAVIGATION & SHORTCUTS
        const handleKeyDown = (e) => {
            const tag = document.activeElement?.tagName.toLowerCase();
            if (tag === 'input' || tag === 'select' || tag === 'textarea') {
                return;
            }

            const canvas = canvasRef.current;
            if (!canvas) return;

            const selected = canvas.getSelectedElements();
            const hasSelection = selected && selected.length > 0 && selected[0];

            // --- FEATURE IMPLEMENTATION: COPY (Ctrl+C / Cmd+C) ---
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
                if (hasSelection) {
                    copiedElementsRef.current = selected
                        .filter(Boolean)
                        .map(elem => elem.cloneNode(true));
                }
                return;
            }

            // --- FEATURE IMPLEMENTATION: PASTE (Ctrl+V / Cmd+V) ---
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
                if (copiedElementsRef.current.length > 0) {
                    e.preventDefault();
                    const currentLayer = canvas.getCurrentDrawing()?.getCurrentLayer();
                    if (!currentLayer) return;

                    const pastedElements = [];

                    copiedElementsRef.current.forEach((originalElem) => {
                        const elemClone = originalElem.cloneNode(true);
                        elemClone.setAttribute("id", canvas.getNextId());

                        let tx = 0, ty = 0;
                        const transform = elemClone.getAttribute('transform');
                        if (transform && transform.includes('translate')) {
                            const matches = transform.match(/translate\(([^,)]+),?([^)]+)?\)/);
                            if (matches) {
                                tx = parseFloat(matches[1]) || 0;
                                ty = parseFloat(matches[2]) || 0;
                            }
                        }
                        elemClone.setAttribute('transform', `translate(${tx + 20}, ${ty + 20})`);

                        currentLayer.appendChild(elemClone);
                        pastedElements.push(elemClone);
                    });

                    canvas.updateCanvas();
                    canvas.selectOnly(pastedElements);
                    canvas.setMode('select');

                    handlerRefs.current.syncLayersFromCanvas();
                    update3DModel();

                    copiedElementsRef.current = pastedElements.map(elem => elem.cloneNode(true));
                }
                return;
            }

            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (hasSelection) {
                    e.preventDefault();
                    canvas.deleteSelectedElements();
                }
                return;
            }

            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                if (!hasSelection) return;

                e.preventDefault();
                const step = e.shiftKey ? 10 : 2;
                let dx = 0;
                let dy = 0;

                switch (e.key) {
                    case 'ArrowLeft': dx = -step; break;
                    case 'ArrowRight': dx = step; break;
                    case 'ArrowUp': dy = -step; break;
                    case 'ArrowDown': dy = step; break;
                    default: return;
                }

                try {
                    canvas.moveSelectedElements(dx, dy, false);
                    canvas.updateCanvas();
                    update3DModel();
                } catch (error) {
                    console.error("Keyboard movement failed:", error);
                }
            }
        };

        // 2. MOUSE SCROLL WHEEL ZOOM
        const handleWheel = (e) => {
            e.preventDefault();

            const zoomDirection = e.deltaY < 0 ? 1 : -1;
            const zoomFactor = 0.1;

            setZoom((currentZoom) => {
                const nextZoom = Math.max(1.0, Math.min(5, currentZoom + (zoomDirection * zoomFactor)));
                if (canvasRef.current) {
                    canvasRef.current.setZoom(nextZoom);
                    canvasRef.current.updateCanvas();

                    const rootSvg = containerRef.current?.querySelector('svg');
                    if (rootSvg) {
                        rootSvg.setAttribute('width', width * nextZoom);
                        rootSvg.setAttribute('height', height * nextZoom);
                    }
                }
                return nextZoom;
            });

            update3DModel();
        };

        // 3. RIGHT BUTTON DRAG PAN CONTROLS
        let isRightDragging = false;
        let startX = 0;
        let startY = 0;
        let startScrollLeft = 0;
        let startScrollTop = 0;

        const handleMouseDown = (e) => {
            if (e.button === 2) {
                isRightDragging = true;
                startX = e.clientX;
                startY = e.clientY;
                if (viewportMainRef.current) {
                    startScrollLeft = viewportMainRef.current.scrollLeft;
                    startScrollTop = viewportMainRef.current.scrollTop;
                }
            }
        };

        const handleMouseMove = (e) => {
            if (!isRightDragging || !viewportMainRef.current) return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;

            viewportMainRef.current.scrollLeft = startScrollLeft - dx;
            viewportMainRef.current.scrollTop = startScrollTop - dy;
        };

        const handleMouseUpOrLeave = (e) => {
            if (e.button === 2 || e.type === 'mouseleave') {
                isRightDragging = false;
            }
        };

        const handleContextMenu = (e) => {
            e.preventDefault();
        };

        window.addEventListener('keydown', handleKeyDown);

        const containerMount = containerRef.current;
        if (containerMount) {
            containerMount.addEventListener('wheel', handleWheel, { passive: false });
        }

        const mainViewport = viewportMainRef.current;
        if (mainViewport) {
            mainViewport.addEventListener('mousedown', handleMouseDown);
            mainViewport.addEventListener('mousemove', handleMouseMove);
            mainViewport.addEventListener('mouseup', handleMouseUpOrLeave);
            mainViewport.addEventListener('mouseleave', handleMouseUpOrLeave);
            mainViewport.addEventListener('contextmenu', handleContextMenu);
        }

        const updateCanvasOffsets = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                canvas.xyz_offsets = {
                    x: typeof rect.left === 'number' && !isNaN(rect.left) ? rect.left : 0,
                    y: typeof rect.top === 'number' && !isNaN(rect.top) ? rect.top : 0
                };
            }
        };

        const initTimer = setTimeout(() => {
            updateCanvasOffsets();
            canvas.updateCanvas();
        }, 100);

        let resizeTimer;
        const handleDebouncedResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                updateCanvasOffsets();
            }, 150);
        };

        window.addEventListener('resize', handleDebouncedResize);

        canvas.bind('changed', canvasChangeProxy);
        canvas.bind('selected', canvasChangeProxy);

        return () => {
            clearTimeout(initTimer);
            clearTimeout(resizeTimer);
            window.removeEventListener('resize', handleDebouncedResize);
            window.removeEventListener('keydown', handleKeyDown);
            if (containerMount) {
                containerMount.removeEventListener('wheel', handleWheel);
            }
            if (mainViewport) {
                mainViewport.removeEventListener('mousedown', handleMouseDown);
                mainViewport.removeEventListener('mousemove', handleMouseMove);
                mainViewport.removeEventListener('mouseup', handleMouseUpOrLeave);
                mainViewport.removeEventListener('mouseleave', handleMouseUpOrLeave);
                mainViewport.removeEventListener('contextmenu', handleContextMenu);
            }

            if (canvasRef.current) {
                canvasRef.current.unbind?.('changed', canvasChangeProxy);
                canvasRef.current.unbind?.('selected', canvasChangeProxy);
            }
            canvasRef.current = null;
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [width, height, addDielineLayer, canvasChangeProxy]);

    const importSvgAndPreserveGradients = useCallback((svgString) => {
        const canvas = canvasRef.current;
        const currentLayer = canvas?.getCurrentDrawing()?.getCurrentLayer();
        if (!canvas || !currentLayer) return null;

        const parser = new DOMParser();
        const doc = parser.parseFromString(svgString, 'image/svg+xml');
        const importedSvg = doc.documentElement;

        if (importedSvg.tagName.toLowerCase() !== 'svg') {
            throw new Error("Invalid SVG file structure.");
        }

        const svgRoot = containerRef.current?.querySelector('svg');
        let globalDefs = svgRoot?.querySelector('defs');
        if (!globalDefs && svgRoot) {
            globalDefs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
            svgRoot.insertBefore(globalDefs, svgRoot.firstChild);
        }

        const idMap = new Map();
        const elementsWithId = importedSvg.querySelectorAll('[id]');

        elementsWithId.forEach((elem) => {
            const oldId = elem.getAttribute('id');
            const newId = canvas.getNextId();
            idMap.set(oldId, newId);
            elem.setAttribute('id', newId);
        });

        const allNodes = importedSvg.querySelectorAll('*');
        allNodes.forEach((node) => {
            ['fill', 'stroke', 'filter', 'clip-path'].forEach((attr) => {
                const attrValue = node.getAttribute(attr);
                if (attrValue && attrValue.includes('url(#')) {
                    const match = attrValue.match(/url\(#([^)]+)\)/);
                    if (match && match[1]) {
                        const oldId = match[1];
                        if (idMap.has(oldId)) {
                            node.setAttribute(attr, `url(#${idMap.get(oldId)})`);
                        }
                    }
                }
            });

            ['href', 'xlink:href'].forEach((attr) => {
                const attrValue = node.getAttribute(attr);
                if (attrValue && attrValue.startsWith('#')) {
                    const oldId = attrValue.substring(1);
                    if (idMap.has(oldId)) {
                        node.setAttribute(attr, `#${idMap.get(oldId)}`);
                    }
                }
            });
        });

        const structuralSelectors = 'linearGradient, radialGradient, clipPath, filter, pattern';
        const definitions = importedSvg.querySelectorAll(structuralSelectors);
        definitions.forEach((def) => {
            if (globalDefs) globalDefs.appendChild(def);
        });

        const svgNS = "http://www.w3.org/2000/svg";
        const newGroup = document.createElementNS(svgNS, "g");
        newGroup.setAttribute("id", canvas.getNextId());
        newGroup.setAttribute("style", "pointer-events: inherit;");
        newGroup.setAttribute("transform", "translate(100, 100)");

        while (importedSvg.firstChild) {
            const child = importedSvg.firstChild;
            if (child.nodeType === Node.ELEMENT_NODE &&
                ['lineargradient', 'radialgradient', 'clippath', 'filter', 'pattern', 'defs'].includes(child.tagName.toLowerCase())) {
                importedSvg.removeChild(child);
            } else {
                newGroup.appendChild(child);
            }
        }

        currentLayer.appendChild(newGroup);
        return newGroup;
    }, []);

    const addShapeToCanvas = (shapeType) => {
        const canvas = canvasRef.current;
        const currentLayer = canvas?.getCurrentDrawing()?.getCurrentLayer();
        if (!canvas || !currentLayer) {
            console.error("No active layer found to add the shape to.");
            return;
        }

        const svgNS = "http://www.w3.org/2000/svg";
        let newElement = null;

        const strokeColor = strokeInputRef.current?.value || "#000000";
        const fillColor = fillInputRef.current?.value || "#ff0000";
        const strokeWidth = thicknessInputRef.current?.value || "3";

        switch (shapeType) {
            case 'rect':
                newElement = document.createElementNS(svgNS, "rect");
                newElement.setAttribute("x", "150");
                newElement.setAttribute("y", "150");
                newElement.setAttribute("width", "120");
                newElement.setAttribute("height", "80");
                break;
            case 'circle':
                newElement = document.createElementNS(svgNS, "circle");
                newElement.setAttribute("cx", "200");
                newElement.setAttribute("cy", "200");
                newElement.setAttribute("r", "50");
                break;
            case 'triangle':
                newElement = document.createElementNS(svgNS, "polygon");
                newElement.setAttribute("points", "200,130 250,230 150,230");
                break;
            case 'star':
                newElement = document.createElementNS(svgNS, "polygon");
                newElement.setAttribute("points", "200,130 215,170 255,170 223,195 235,235 200,210 165,235 177,195 145,170 185,170");
                break;
            case 'feather':
                newElement = document.createElementNS(svgNS, "path");
                newElement.setAttribute("d", "M213.000 0.783 C 213.000...[clipped value for brevity]");
                break;
            default:
                return;
        }

        if (newElement) {
            newElement.setAttribute("id", canvas.getNextId());
            newElement.setAttribute("fill", fillColor);
            newElement.setAttribute("stroke", strokeColor);
            newElement.setAttribute("stroke-width", strokeWidth);
            newElement.setAttribute("style", "pointer-events: inherit;");

            currentLayer.appendChild(newElement);

            canvas.updateCanvas();
            canvas.selectOnly([newElement]);
            canvas.setMode('select');

            canvas.updateCanvas();
            syncLayersFromCanvas();
            zooming(zoom);
        }

        setIsShapeMenuOpen(false);
    };

    const popupPosition = useMemo(() => {
        if (!shapeButtonRef.current) return { top: '0px', left: '0px' };
        const rect = shapeButtonRef.current.getBoundingClientRect();
        return {
            top: `${rect.top}px`,
            left: `${rect.right + 8}px`
        };
    }, [isShapeMenuOpen]);

    const triggerImageUpload = () => {
        fileInputRef.current?.click();
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file || !canvasRef.current) return;

        const reader = new FileReader();

        if (file.type === 'image/svg+xml') {
            reader.onload = (event) => {
                const svgString = event.target.result;
                try {
                    const newGroup = importSvgAndPreserveGradients(svgString);
                    if (!newGroup) return;

                    const canvas = canvasRef.current;
                    canvas.updateCanvas();
                    canvas.selectOnly([newGroup]);
                    canvas.setMode('select');

                    syncLayersFromCanvas();
                    zooming(zoom);
                } catch (err) {
                    console.error("Failed to parse and inject uploaded SVG:", err);
                }
            };
            reader.readAsText(file);
        } else {
            reader.onload = (event) => {
                const dataUrl = event.target.result;
                const canvas = canvasRef.current;

                const currentLayer = canvas.getCurrentDrawing().getCurrentLayer();
                if (!currentLayer) {
                    console.error("No active layer found to add the image to.");
                    return;
                }

                const svgNS = "http://www.w3.org/2000/svg";
                const newImage = document.createElementNS(svgNS, "image");

                newImage.setAttribute("x", "100");
                newImage.setAttribute("y", "100");
                newImage.setAttribute("width", "250");
                newImage.setAttribute("height", "250");
                newImage.setAttribute("id", canvas.getNextId());
                newImage.setAttribute("style", "pointer-events: inherit;");

                if (typeof canvas.setHref === 'function') {
                    canvas.setHref(newImage, dataUrl);
                } else {
                    newImage.setAttribute("href", dataUrl);
                    newImage.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", dataUrl);
                }

                currentLayer.appendChild(newImage);

                canvas.updateCanvas();
                canvas.selectOnly([newImage]);
                canvas.setMode('select');
                syncLayersFromCanvas();
                zooming(zoom);
            };
            reader.readAsDataURL(file);
        }

        e.target.value = '';
    };

    const addNewLayer = () => {
        const name = prompt("Enter new layer name:");
        if (!name) return;
        addLayer(name);
    };

    const addLayer = (name) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.createLayer(name);
        canvas.identifyLayers();
        canvas.setCurrentLayer(name);
        canvas.updateCanvas();

        syncLayersFromCanvas();
    };

    const selectLayer = (layerName) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.setCurrentLayer(layerName);
        setActiveLayer(layerName);
    };

    const deleteActiveLayer = () => {
        const canvas = canvasRef.current;
        const drawing = canvas?.getCurrentDrawing();
        if (!canvas || !drawing) return;

        if (drawing.getNumLayers() <= 1) {
            console.warn("Cannot delete the last remaining layer.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete the layer "${activeLayer}"?`)) {
            canvas.deleteCurrentLayer();
            canvas.identifyLayers();
            syncLayersFromCanvas();
        }
    };

    const changeTool = (tool) => {
        canvasRef.current?.setMode(tool);
    };
    const deleteSelected = () => canvasRef.current?.deleteSelectedElements();
    const handleUndo = () => canvasRef.current?.undoMgr.undo();
    const handleRedo = () => canvasRef.current?.undoMgr.redo();

    const handleColorChange = (type, value) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set the lock so the canvas updates don't trip up input field mutations
        isUpdatingPropsRef.current = true;

        const selected = canvas.getSelectedElements();

        // Apply color change safely
        canvas.setColor(type, value, true);
        canvas.setMode('select');

        if (selected && selected.length > 0) {
            canvas.selectOnly(selected, true);
        }

        canvas.updateCanvas();
        update3DModel();

        // Release the lock
        setTimeout(() => {
            isUpdatingPropsRef.current = false;
        }, 50);
    };

    const handleTextChange = (e) => {
        canvasRef.current?.setTextContent(e.target.value);
    };

    const handleFontChange = (e) => {
        canvasRef.current?.setFontFamily(e.target.value);
    };

    const moveForward = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const selected = canvas.getSelectedElements();
        if (!selected || selected.length === 0 || !selected[0]) {
            console.warn("No elements selected to move forward.");
            return;
        }

        let changed = false;
        for (let i = selected.length - 1; i >= 0; i--) {
            const elem = selected[i];
            const parent = elem.parentNode;
            if (!parent) continue;

            const nextSibling = elem.nextSibling;
            if (nextSibling) {
                parent.insertBefore(elem, nextSibling.nextSibling);
                changed = true;
            }
        }

        if (changed) {
            canvas.updateCanvas();
            canvas.selectOnly(selected);
            update3DModel();
        }
    };

    const moveBackward = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const selected = canvas.getSelectedElements();
        if (!selected || selected.length === 0 || !selected[0]) {
            console.warn("No elements selected to move backward.");
            return;
        }

        let changed = false;
        for (let i = 0; i < selected.length; i++) {
            const elem = selected[i];
            const parent = elem.parentNode;
            if (!parent) continue;

            const prevSibling = elem.previousSibling;
            if (prevSibling) {
                parent.insertBefore(elem, prevSibling);
                changed = true;
            }
        }

        if (changed) {
            canvas.updateCanvas();
            canvas.selectOnly(selected);
            update3DModel();
        }
    };

    const submitAiRequest = async () => {
        if (!popupInputValue.trim()) return;

        setIsAiLoading(true);

        try {
            const dataAccess = new ApiDataAccess('api/dieline');
            const svgString = await dataAccess.post('/ai', { text: popupInputValue });
            if (!svgString) return;

            const newGroup = importSvgAndPreserveGradients(svgString);
            if (!newGroup) return;

            const canvas = canvasRef.current;
            canvas.updateCanvas();
            canvas.selectOnly([newGroup]);
            canvas.setMode('select');

            syncLayersFromCanvas();
            zooming(zoom);

            setIsCustomPopupOpen(false);
            setPopupInputValue('');
        } catch (error) {
            console.error("Failed to submit AI request:", error);
            alert("Generation failed. Please try again.");
        } finally {
            setIsAiLoading(false);
        }
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
        update3DModel();
    }

   

    const importSvg = async (svgData) => {
        const canvas = canvasRef.current;

        // 1. Import the raw string from Spire directly
        canvas.importSvgString(svgData);

        // 2. Give SVG-Edit time to render its <use> wrapper
        setTimeout(() => {
            canvas.selectAllInCurrentLayer();
            let selected = canvas.getSelectedElements();

            if (selected.length > 0 && selected[0]) {
                let selectedEl = selected[0];

                // --- THE FIX: Unpack SVG-Edit's Import Wrapper ---
                // If SVG-Edit wrapped our import in a <use> tag...
                if (selectedEl.tagName.toLowerCase() === 'use') {
                    console.log("Unpacking SVG-Edit's <use> wrapper...");

                    const href = selectedEl.getAttribute('href') || selectedEl.getAttribute('xlink:href');

                    if (href && href.startsWith('#')) {
                        const hiddenId = href.substring(1);

                        // Look inside SVG-Edit's DOM for the hidden imported document
                        const svgRoot = canvas.getSvgRoot();
                        const hiddenElement = svgRoot.querySelector(`#${hiddenId}`);

                        if (hiddenElement) {
                            // Create a standard group <g> to replace the wrapper
                            const newGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

                            // Keep the scale/transform that SVG-Edit calculated
                            const transform = selectedEl.getAttribute('transform');
                            if (transform) newGroup.setAttribute('transform', transform);

                            // Extract all the actual PDF vectors out of hiding
                            Array.from(hiddenElement.childNodes).forEach(child => {
                                newGroup.appendChild(child.cloneNode(true));
                            });

                            // Swap the <use> tag for our new <g> tag directly on the canvas
                            selectedEl.parentNode.replaceChild(newGroup, selectedEl);

                            // Clean up the hidden defs to save memory
                            hiddenElement.remove();

                            // Tell SVG-Edit to select our new group instead
                            canvas.clearSelection();
                            canvas.addToSelection([newGroup], true);
                            selected = canvas.getSelectedElements();

                            console.log("✅ Successfully unpacked into an editable group!");
                        }
                    }
                }
                // -------------------------------------------------

                // Now that we guarantee it's a <g> tag, we can run the ungrouping loop!
                let safetyCounter = 0;

                while (
                    selected.length > 0 &&
                    selected[0] &&
                    (selected[0].tagName?.toLowerCase() === 'g' || selected[0].nodeName?.toLowerCase() === 'g') &&
                    safetyCounter < 15
                ) {
                    console.log(`UNGROUPING Level ${safetyCounter + 1}`);

                    canvas.ungroupSelectedElement();
                    canvas.selectAllInCurrentLayer();
                    selected = canvas.getSelectedElements();

                    safetyCounter++;
                }
            }

            // 3. Clear the selection box so the user can interact cleanly
            canvas.clearSelection();

        }, 300); // 300ms delay to ensure SVG-Edit is ready
    };

    return (
        <div style={styles.appLayout}>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                onChange={handleImageUpload}
            />

            <header style={styles.header}>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#FFF' }}>Artwork Designer POC</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleUndo} style={styles.btn}>&#x21A9; Undo</button>
                    <button onClick={handleRedo} style={styles.btn}>&#x21AA; Redo</button>
                    <button onClick={deleteSelected} style={{ ...styles.btn, backgroundColor: '#ff4d4d', color: '#fff', border: 'none' }}>&#x1F5D1; Delete</button>
                    <button onClick={exportSVG} style={{ ...styles.btn, backgroundColor: '#2ecc71', color: '#fff', border: 'none', marginLeft: '10px', fontWeight: 'bold' }}>&#x1F4BE; Export SVG</button>
                </div>
            </header>

            

            <aside style={{ ...styles.sidebar, flexShrink: 0, flexGrow: 0 }}>
                <h4 style={styles.sectionTitle}>Tools</h4>
                <button onClick={() => { setIsModalOpen(true) }} style={styles.toolBtn}>IMPORT</button>
                {['select', 'rect', 'circle', 'ellipse', 'line', 'path', 'text'].map((tool) => (
                    <button
                        key={tool}
                        onClick={() => changeTool(tool)}
                        style={styles.toolBtn}
                    >
                        {tool.toUpperCase()}
                    </button>
                ))}

                <button onClick={triggerImageUpload} style={styles.toolBtn}>IMAGE</button>
                <button
                    ref={shapeButtonRef}
                    onClick={() => setIsShapeMenuOpen(!isShapeMenuOpen)}
                    style={{ ...styles.toolBtn, backgroundColor: isShapeMenuOpen ? '#e0e0e0' : '' }}
                >
                    ➕ ADD SHAPE
                </button>

                <button
                    onClick={() => setIsCustomPopupOpen(true)}
                    style={{ ...styles.toolBtn, backgroundColor: '#9b59b6', color: '#fff' }}
                >
                    ℹ️ AI
                </button>

                {isShapeMenuOpen && (
                    <div style={{
                        position: 'fixed',
                        top: popupPosition.top,
                        left: popupPosition.left,
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: '4px',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column',
                        width: '120px',
                        padding: '4px'
                    }}>
                        {[
                            { id: 'rect', label: 'Rectangle' },
                            { id: 'circle', label: 'Circle' },
                            { id: 'triangle', label: 'Triangle' },
                            { id: 'star', label: 'Star' },
                            { id: 'feather', label: 'Feather' }
                        ].map((shape) => (
                            <button
                                key={shape.id}
                                onClick={() => addShapeToCanvas(shape.id)}
                                style={{
                                    padding: '8px 12px',
                                    background: 'none',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    borderRadius: '2px',
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.background = 'none'}
                            >
                                {shape.label}
                            </button>
                        ))}
                    </div>
                )}

                <hr style={{ margin: '20px 0', borderColor: '#eee' }} />

                <h4 style={styles.sectionTitle}>Layers</h4>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                    <button onClick={addNewLayer} style={{ ...styles.btn, flex: 1, fontSize: '12px' }}>&#x2795; Add</button>
                    <button onClick={deleteActiveLayer} style={{ ...styles.btn, backgroundColor: '#ff4d4d', color: '#fff', border: 'none', flex: 1, fontSize: '12px' }}>&#x1F5D1; Remove</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '150px', overflowY: 'auto' }}>
                    {canvasLayers.map((layerName) => (
                        <div
                            key={layerName}
                            onClick={() => selectLayer(layerName)}
                            style={{
                                padding: '6px 10px',
                                background: activeLayer === layerName ? '#3498db' : '#f5f5f5',
                                color: activeLayer === layerName ? '#fff' : '#333',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontWeight: activeLayer === layerName ? 'bold' : 'normal'
                            }}
                        >
                            <span>{layerName}</span>
                            {activeLayer === layerName && <span>✓</span>}
                        </div>
                    ))}
                </div>
            </aside>

            <main
                ref={viewportMainRef}
                style={{ ...styles.canvasContainer, overflow: 'auto', display: 'block', position: 'relative', cursor: 'grab' }}
            >
                <div style={{ display: 'grid', gridTemplateColumns: '25px 1fr', gridTemplateRows: '25px 1fr', width: 'max-content', height: 'max-content' }}>
                    <div style={{ background: '#f0f0f0', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, left: 0, zIndex: 10 }} />

                    <div style={{ background: '#f0f0f0', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, zIndex: 9, height: '25px', width: `${width * zoom}px`, display: 'flex', alignItems: 'flex-end', fontSize: '9px', fontFamily: 'monospace', color: '#666', overflow: 'hidden' }}>
                        {Array.from({ length: Math.ceil((width * zoom) / 50) }).map((_, i) => (
                            <div key={i} style={{ width: '50px', minWidth: '50px', borderLeft: '1px solid #b3b3b3', height: '10px', paddingLeft: '2px', boxSizing: 'border-box', whiteSpace: 'nowrap' }}>
                                {Math.round((i * 50) / zoom)}
                            </div>
                        ))}
                    </div>

                    <div style={{ background: '#f0f0f0', borderRight: '1px solid #ccc', position: 'sticky', left: 0, zIndex: 9, width: '25px', height: `${height * zoom}px`, display: 'flex', flexDirection: 'column', fontSize: '9px', fontFamily: 'monospace', color: '#666', overflow: 'hidden' }}>
                        {Array.from({ length: Math.ceil((height * zoom) / 50) }).map((_, i) => (
                            <div key={i} style={{ height: '50px', minHeight: '50px', borderTop: '1px solid #b3b3b3', paddingTop: '2px', paddingLeft: '2px', boxSizing: 'border-box' }}>
                                {Math.round((i * 50) / zoom)}
                            </div>
                        ))}
                    </div>

                    <div
                        ref={containerRef}
                        style={{
                            ...styles.canvasMount,
                            display: 'block',
                            width: 'max-content',
                            height: 'max-content'
                        }}
                    />
                </div>
            </main>

            <aside style={styles.inspectorPanel}>
                <h4 style={styles.sectionTitle}>Canvas Zoom</h4>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', background: '#f9f9f9', padding: '8px', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                    <button onClick={() => handleZoomChange(zoom - 0.1)} style={{ ...styles.btn, padding: '2px 8px', flexShrink: 0 }}>-</button>
                    <input
                        type="range"
                        min="1"
                        max="5"
                        step="0.1"
                        value={zoom}
                        onChange={(e) => handleZoomChange(e.target.value)}
                        style={{ flexGrow: 1, minWidth: '0', cursor: 'pointer' }}
                    />
                    <button onClick={() => handleZoomChange(zoom + 0.1)} style={{ ...styles.btn, padding: '2px 8px', flexShrink: 0 }}>+</button>
                    <span style={{ fontSize: '12px', minWidth: '40px', textAlign: 'right', fontFamily: 'monospace', marginLeft: '2px' }}>{Math.round(zoom * 100)}%</span>
                </div>

                <hr style={{ margin: '15px 0', borderColor: '#eee' }} />
                <h4 style={styles.sectionTitle}>Arrange</h4>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                    <button onClick={moveForward} style={{ ...styles.btn, flex: 1 }}>Bring Forward</button>
                    <button onClick={moveBackward} style={{ ...styles.btn, flex: 1 }}>Send Backward</button>
                </div>

                <hr style={{ margin: '15px 0', borderColor: '#eee' }} />

                <h4 style={styles.sectionTitle}>Properties</h4>

                <label style={styles.label}>Text Content</label>
                <input
                    ref={textInputRef}
                    type="text"
                    disabled
                    placeholder="Select a text node..."
                    onChange={handleTextChange}
                    style={styles.textInput}
                />

                <label style={styles.label}>Font Family</label>
                <select
                    ref={fontFamilyInputRef}
                    disabled
                    onChange={handleFontChange}
                    style={styles.selectInput}
                >
                    <option value="sans-serif">Sans-Serif</option>
                    <option value="serif">Serif</option>
                    <option value="monospace">Monospace</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Times New Roman">Times New Roman</option>
                </select>

                <label style={styles.label}>Fill Color</label>
                <input
                    ref={fillInputRef}
                    type="color"
                    defaultValue="#ff0000"
                    onChange={(e) => handleColorChange('fill', e.target.value)}
                    style={styles.colorInput}
                />

                <label style={styles.label}>Stroke Color</label>
                <input
                    ref={strokeInputRef}
                    type="color"
                    defaultValue="#000000"
                    onChange={(e) => handleColorChange('stroke', e.target.value)}
                    style={styles.colorInput}
                />

                <label style={styles.label}>Stroke Thickness</label>
                <input
                    ref={thicknessInputRef}
                    type="range"
                    min="1"
                    max="25"
                    defaultValue="3"
                    onChange={(e) => canvasRef.current?.setStrokeWidth(Number(e.target.value))}
                    style={{ width: '100%', cursor: 'pointer' }}
                />
            </aside>

            {isCustomPopupOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                        width: '320px',
                        textAlign: 'center',
                        position: 'relative'
                    }}>
                        {isAiLoading ? (
                            <div style={{ padding: '20px 0' }}>
                                <div style={{
                                    display: 'inline-block',
                                    width: '40px',
                                    height: '40px',
                                    border: '4px solid #f3f3f3',
                                    borderTop: '4px solid #e74c3c',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginBottom: '16px'
                                }} />
                                <style>{`
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                `}</style>
                                <h4 style={{ margin: 0, color: '#333' }}>Generating SVG...</h4>
                                <p style={{ fontSize: '13px', color: '#666' }}>Powered by ePac AI</p>
                            </div>
                        ) : (
                            <>
                                <h3 style={{ marginTop: 0, color: '#333' }}>AI Creator</h3>
                                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', marginBottom: '12px' }}>
                                    Enter your shape request below
                                </p>

                                <input
                                    type="text"
                                    disabled={isAiLoading}
                                    value={popupInputValue}
                                    onChange={(e) => setPopupInputValue(e.target.value)}
                                    placeholder="Type data details here..."
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        marginBottom: '20px'
                                    }}
                                />

                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    <button
                                        onClick={() => setIsCustomPopupOpen(false)}
                                        style={{
                                            ...styles.btn,
                                            backgroundColor: '#bdc3c7',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '8px 20px',
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={submitAiRequest}
                                        disabled={isAiLoading || !popupInputValue.trim()}
                                        style={{
                                            ...styles.btn,
                                            backgroundColor: !popupInputValue.trim() ? '#f5b7b1' : '#e74c3c',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '8px 20px',
                                            fontWeight: 'bold',
                                            cursor: !popupInputValue.trim() ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className={fullscreen ? "parent-container popup" : "parent-container"} >
                {fullscreen && <div className='backsplash' onClick={() => { toggleFullscreen() }}></div>}
                <div className={fullscreen ? "centered-child popup" : "centered-child"} >
                    <StandupPouchModel
                        id='test'
                        ref={rendererRef}
                        viewWidth={fullscreen ? 800 : 300}
                        viewHeight={fullscreen ? 800 : 300}
                        animationSpeed={0.005}
                        backgroundColor={'ffffff'}
                        width={200}
                        height={400}
                        gusset={80}
                        tearNotch={1}
                        zipper={1}
                        hangHole={1}
                        valve={1}
                        roundedCorners={1}
                        sealWidth={1}
                        finishType={1}
                        pouchColor={'c2730a'}
                    />
                    {!fullscreen && <button className='button' onClick={() => { toggleFullscreen() }}>Full Screen</button>}
                </div>

                <PdfUploader
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={(svgData) => {
                        importSvg(svgData);
                        setIsModalOpen(false);                                               
                    }}
                />

            </div>
        </div>
    );
};

export default VectorStudioFinal;